<template>
  <a-config-provider :locale="locale" :getPopupContainer="getPopupContainerFn" :theme="localTheme">
    <a-app>
      <a-layout class="h-100vh flex flex-col bg-cover bg-no-repeat">
        <a-layout-header :style="headerStyle" class="border-b-1 shadow-2xl" v-if="!hideLayout">
          <Header />
        </a-layout-header>
        <template v-if="hideLayout">
          <RouterView />
        </template>
        <template v-else>
          <template v-if="showLoginedMenu">
            <a-layout class="h-full">
              <a-layout-sider
                class="w-250px bg-transparent! h-full custom-sider hidden md:block"
                :collapsible="true"
                v-model:collapsed="collapsed"
                @collapse="onCollapse"
              >
                <VMenu />
                <template #trigger>
                  <a-button type="primary" ghost block class="b-none p-0">
                    <MenuFoldOutlined v-if="collapsed" />
                    <MenuUnfoldOutlined v-else />
                  </a-button>
                </template>
              </a-layout-sider>
              <a-layout-content class="flex-1 overflow-y-auto bg-c">
                <Page>
                  <Background />
                  <div class="relative z-1">
                    <RouterView />
                  </div>
                  <a-layout-footer
                    v-if="!hideFooter"
                    class="bg-transparent! flex sm:hidden p-0! relative top-8"
                  >
                    <Footer />
                  </a-layout-footer>
                </Page>
              </a-layout-content>
            </a-layout>
          </template>
          <a-layout-content v-else class="flex-1 overflow-y-auto">
            <RouterView />
            <!-- <a-layout-footer v-if="!hideFooter" class="bg-transparent flex sm:hidden p-0!">
              <Footer />
            </a-layout-footer> -->
          </a-layout-content>
          <!-- <a-layout-footer v-if="!hideFooter" class="bg-transparent hidden sm:flex p-0!">
            <Footer />
          </a-layout-footer> -->
        </template>
      </a-layout>
    </a-app>
  </a-config-provider>
</template>
<script setup lang="ts">
import type { CSSProperties } from "vue";
// import mainBg from "@/assets/images/main-bg.png";
import { useWindowSize } from "@vueuse/core";
import i18n from "@/locales";
import { antdLocales } from "@/locales/antd";

const { token } = theme.useToken();
// 将locale改为响应式变量，而非计算属性，使其可以被修改
const locale = ref(antdLocales[i18n.global.locale.value] || antdLocales.en);
const color = systemStorage.value.primaryColor || "#2E8B57";
const isLight = systemStorage.value.theme === "light";
const { showLoginedMenu } = useMenus();
const collapsed = ref(false);

// const mainBgPic = computed(() => mainBg);

const { width } = useWindowSize();
const isMobile = computed(() => width.value < 768);

const route = useRoute();

const hideLayout = computed(() => !!route.meta.hideLayout);
const hideFooter = computed(() => !!route.meta.hideFooter);

const localTheme = ref({
  token: { colorPrimary: color || theme.defaultConfig.token.colorPrimary },
  isDark: !isLight,
  algorithm: !isLight ? theme.darkAlgorithm : theme.defaultAlgorithm,
});

provide("locale", locale);
provide("theme", localTheme);
provide("collapsed", collapsed);
provide("isMobile", isMobile);

const headerStyle = computed<CSSProperties>(() => {
  return {
    height: "48px",
    paddingInline: "0px",
    lineHeight: "48px",
    borderBottom: `1px solid ${token.value.colorBorder}`,
    backgroundColor: token.value.colorBgContainer,
  };
});

function onCollapse(val: boolean) {
  collapsed.value = val;
}

function getPopupContainerFn(node?: HTMLElement) {
  return node ? (node.parentNode as HTMLElement) : document.body;
}
function getCssVariableValue(value: any) {
  if (typeof value === "string") {
    return value;
  } else if (typeof value === "number") {
    return `${value}px`;
  } else {
    return value;
  }
}

// 将十六进制颜色转换为RGB值
function hexToRgb(hex: string): string | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : null;
}

function setCssVariables() {
  const id = "AntTokenAsCssVariables";
  let styleTag = document.head.querySelector(`#${id}`) as HTMLStyleElement;
  if (!styleTag) {
    styleTag = document.createElement("style");
    styleTag.id = id;
    document.head.appendChild(styleTag);
  }

  // 生成CSS变量
  const cssVars = Object.entries(token.value)
    .map(([key, value]) => `--${key}: ${getCssVariableValue(value)};`)
    .join("");

  // 为主色调添加RGB变量
  const primaryColor = token.value.colorPrimary;
  const primaryColorRgb = hexToRgb(primaryColor);
  const rgbVar = primaryColorRgb ? `--colorPrimary-rgb: ${primaryColorRgb};` : "";

  styleTag.innerHTML = `:root { ${cssVars} ${rgbVar} }`;
}
watchEffect(setCssVariables);

// 监听i18n语言变化，更新locale
watch(
  () => i18n.global.locale.value,
  newLocale => {
    locale.value = antdLocales[newLocale] || antdLocales.en;
  }
);
</script>
